// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useState, useMemo } from 'react';

import {
  type EvolveApiRoute,
  type EvolveApiResponse,
  useEvolvePaginatedGet,
  type GetPaginatedOpts,
} from '@evolvemep/api-ts';
import { DragDropContext, Droppable, type OnDragEndResponder } from '@hello-pangea/dnd';
import { Card, Flex, LoadingOverlay, type MantineColor, Text } from '@mantine/core';

import { KANBAN_LABEL_HEIGHT } from 'components/Mantine/Kanban/constants';
import { CountIndicatorAndCollapseButton } from 'components/Mantine/Kanban/CountIndicatorAndCollapseButton';
import type { CardConfigMapper } from 'components/Mantine/Kanban/types';
import type { KeysOfType } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

import { AgGridStyleTooltip } from '../AgGridStyleTooltip';

type Props<
  LaneRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Lane extends ([LaneRoute] extends [never] ? unknown : EvolveApiResponse<'GET_PAGINATED', LaneRoute>['data'][number]),
  CardRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Card extends EvolveApiResponse<'GET_PAGINATED', CardRoute>['data'][number],
  CardKey extends KeysOfType<Card, string>,
> = {
  /** Unique ID for the lane. */
  id: string;
  /** The data representing the kanban lane itself. */
  lane: Lane;
  /** Label to display at the top of the lane. */
  label: string;
  /** Optional color for the lane header. */
  color?: MantineColor;
  /** The key of the ID property of the kanban card. The value of the property must be a string. */
  cardIdKey: CardKey;
  /** Callback used to get config for fetching new cards. */
  cardConfigMapper: CardConfigMapper<Lane, CardRoute>;
  /** Callback function to render the kanban cards. */
  children: (cards: Card[]) => React.ReactNode;
  /** Optional callback invoked when a card is dragged and dropped to a new position. */
  onDragEnd?: (cardId: Card[CardKey], precedingCardId?: Card[CardKey]) => Promise<void>;
  /** Optional tooltip text to display on the lane when dragging a card. */
  draggingTooltip?: string;
};

export const KanbanLane = <
  LaneRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Lane extends ([LaneRoute] extends [never] ? unknown : EvolveApiResponse<'GET_PAGINATED', LaneRoute>['data'][number]),
  CardRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  Card extends EvolveApiResponse<'GET_PAGINATED', CardRoute>['data'][number],
  CardKey extends KeysOfType<Card, string>,
>({
  children,
  cardIdKey,
  id,
  label,
  color,
  onDragEnd,
  draggingTooltip,
  lane,
  cardConfigMapper,
}: Props<LaneRoute, Lane, CardRoute, Card, CardKey>) => {
  // Used to actually display the data for optimistic UI updates.
  const [cards, setCards] = useState<Card[]>([]);
  const [dragging, setDragging] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const cardWidth = collapsed ? KANBAN_LABEL_HEIGHT : 350;

  const config = useMemo(() => cardConfigMapper(lane), [lane, cardConfigMapper]);
  const defaultOpts = useMemo(
    () => ({ defaultConfig: config.requestConfig }) as GetPaginatedOpts<CardRoute>,
    [config.requestConfig],
  );
  const { data, entireCount, loading, fetchNextPage, reset, setDefaultOpts } = useEvolvePaginatedGet<CardRoute>(
    config.requestConfig.url as CardRoute,
    defaultOpts,
  );
  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  useEffect(() => {
    // Always fetch the first page on changes
    void fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    if (loading || saving) return;

    // Sync UI with the latest data.
    setCards(data as Card[]);
  }, [data, loading, saving]);

  useEffect(() => {
    if (!loading && isNotNil(entireCount)) {
      setCollapsed(entireCount === 0);
    }
  }, [entireCount, loading]);

  const handleDragEnd: OnDragEndResponder = async ({ source, destination, draggableId }) => {
    const cardId = draggableId as unknown as Card[CardKey];
    setDragging(false);

    if (isNil(destination) || source.index === destination.index) return;

    const precedingCard = cards[source.index > destination.index ? destination.index - 1 : destination.index];
    const precedingCardId = isNotNil(precedingCard) ? precedingCard[cardIdKey] : undefined;

    // Make the optimistic UI update.
    setCards((prevCards) => {
      const updatedCards = [...prevCards];

      // Remove the dragged card from its original position and insert it at the new position.
      const [movedCard] = updatedCards.splice(source.index, 1);
      if (!movedCard) return prevCards;

      updatedCards.splice(destination.index, 0, movedCard);
      return updatedCards;
    });

    if (isNil(onDragEnd)) return;

    setSaving(true);
    await onDragEnd(cardId, precedingCardId)
      .finally(reset)
      .finally(() => setSaving(false));
  };

  return (
    <AgGridStyleTooltip
      hidden={!dragging}
      openDelay={0}
      label={draggingTooltip ?? 'Drag card within column to reorder'}
      position="top"
    >
      <Flex direction="column" style={{ height: '100%', width: cardWidth, transition: 'width 0.25s ease' }}>
        <Flex mb={4} style={{ maxWidth: cardWidth }}>
          <Flex
            className="hover-title"
            align="center"
            justify="space-between"
            gap="sm"
            onClick={() => setCollapsed(!collapsed)}
            style={{
              height: KANBAN_LABEL_HEIGHT,
              width: collapsed ? undefined : '100%',
              transformOrigin: 'left bottom',
              transform: collapsed ? 'rotate(90deg)' : undefined,
              cursor: 'pointer',
            }}
          >
            {collapsed && (
              <CountIndicatorAndCollapseButton
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                entireCount={entireCount}
                loading={loading}
                rotate
              />
            )}
            <Text
              fw={500}
              c={collapsed && (entireCount ?? 0) === 0 ? 'dimmed' : undefined}
              title={label}
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                userSelect: 'none',
              }}
            >
              {label}
            </Text>
            {!collapsed && (
              <CountIndicatorAndCollapseButton
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                entireCount={entireCount}
                loading={loading}
              />
            )}
          </Flex>
        </Flex>
        {!collapsed && (
          <Card
            withBorder
            display="flex"
            radius="md"
            bg="gray.0"
            p="xs"
            pb={0}
            style={{
              width: cardWidth,
              flexDirection: 'column',
              flex: '1 0 0',
            }}
          >
            {isNotNil(color) && <Card.Section p="xs" bg={color} mb="sm" />}
            <Flex direction="column" style={{ flex: '1 1 auto', overflowY: 'auto', scrollbarWidth: 'none' }}>
              <DragDropContext onDragStart={() => setDragging(true)} onDragEnd={handleDragEnd}>
                <Droppable droppableId={id}>
                  {(provided) => (
                    <Flex
                      direction="column"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{ flex: '1 1 auto', overflowY: 'auto', scrollbarWidth: 'none' }}
                    >
                      {children(cards)}
                      {provided.placeholder}
                      <Flex justify="center">
                        <TriggerOnVisible loading={loading && !saving} onVisible={fetchNextPage} />
                      </Flex>
                    </Flex>
                  )}
                </Droppable>
                <LoadingOverlay
                  visible={dragging}
                  loaderProps={{ style: { display: 'none' } }}
                  transitionProps={{
                    duration: 150,
                    exitDuration: 150,
                  }}
                />
                <LoadingOverlay
                  visible={saving}
                  loaderProps={{ variant: 'bars' }}
                  transitionProps={{
                    duration: 150,
                    exitDuration: 150,
                  }}
                />
              </DragDropContext>
            </Flex>
          </Card>
        )}
      </Flex>
    </AgGridStyleTooltip>
  );
};
